import { type JSX, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import {
  Button,
  Card,
  DateRangePicker,
  Skeleton,
  type DateRangePreset,
} from '@/components/ui';
import {
  useAdminDashboard,
  type AdminDashboardRange,
  type ResellerLowBalanceCustomer,
  type ResellerMarginPrefix,
  type ResellerRecentAuditEntry,
  type ResellerTopCustomer,
} from '@/hooks/useAdminDashboard';
import { ApiError } from '@/lib/api';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

type PresetKey = '7d' | '30d' | '90d' | 'ytd';

// Same UTC-day anchoring as /reports so picking "30d" tomorrow shifts the
// window without a manual refresh. YTD is from Jan 1 00:00 UTC of the
// current year so the bar is stable across the year.
function makePresets(now: Date = new Date()): Record<PresetKey, AdminDashboardRange> {
  const dayMs = 24 * 60 * 60 * 1000;
  const toIso = (d: Date) => d.toISOString();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const yearStart = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
  return {
    '7d': { from: toIso(new Date(today.getTime() - 7 * dayMs)), to: toIso(now) },
    '30d': { from: toIso(new Date(today.getTime() - 30 * dayMs)), to: toIso(now) },
    '90d': { from: toIso(new Date(today.getTime() - 90 * dayMs)), to: toIso(now) },
    ytd: { from: toIso(yearStart), to: toIso(now) },
  };
}

// ---------------------------------------------------------------------------
// Reseller / platform admin home page. Lives at /admin/dashboard and is
// gated by the same RequireReseller middleware as the rest of /v1/admin/*.
// Replaces the per-tenant /dashboard for resellers — the sidebar surfaces
// the link only for tenant_type=platform|reseller.
//
// Single round-trip: useAdminDashboard returns header counts, 30d KPIs,
// top customers by volume, and low-balance alerts in one payload so the
// page paints without waterfall.
// ---------------------------------------------------------------------------
export default function AdminDashboardPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const presets = useMemo(() => makePresets(), []);
  const [presetKey, setPresetKey] = useState<string>('30d');
  const [customRange, setCustomRange] = useState<AdminDashboardRange>(presets['30d']);
  const range: AdminDashboardRange =
    presetKey === 'custom' ? customRange : presets[presetKey as PresetKey];

  const presetLabel: Record<PresetKey, string> = {
    '7d': t('ad_period_7d'),
    '30d': t('ad_period_30d'),
    '90d': t('ad_period_90d'),
    ytd: t('ad_period_ytd'),
  };
  const pickerPresets: DateRangePreset[] = (Object.keys(presets) as PresetKey[]).map(
    (k) => ({ key: k, label: presetLabel[k], range: presets[k] }),
  );

  const dash = useAdminDashboard({ range });

  if (dash.isLoading) {
    return <DashboardSkeleton />;
  }

  if (dash.error || !dash.data) {
    return (
      <div className="px-8 py-8 max-w-[1200px] mx-auto">
        <Card padding="lg">
          <div className="text-center py-10">
            <p className="text-[14px] text-text-2 font-medium">
              {t('ad_load_error_title')}
            </p>
            <p className="text-[13px] text-text-3 mt-1">
              {dash.error instanceof ApiError
                ? dash.error.message
                : t('ad_load_error_sub')}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const {
    totals,
    stats,
    top_customers,
    low_balance,
    recent_audit,
    margin_by_prefix,
  } = dash.data;
  const deliveryRate =
    stats.messages_sent > 0
      ? (stats.messages_delivered / stats.messages_sent) * 100
      : null;

  return (
    <div className="flex flex-col h-full">
      {low_balance.length > 0 && <LowBalanceBanner customers={low_balance} lang={lang} />}

      <div className="px-8 py-8 max-w-[1200px] mx-auto w-full flex-1 overflow-y-auto">
        {/* Header */}
        <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="text-[12px] text-text-3 mb-1.5">
              {t('ad_subtitle', {
                period: new Date().toLocaleDateString(lang, {
                  month: 'long',
                  year: 'numeric',
                }),
              })}
            </div>
            <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
              {t('ad_title')}
            </h1>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <DateRangePicker
              presets={pickerPresets}
              value={presetKey}
              range={range}
              onChange={({ key, range: r }) => {
                setPresetKey(key);
                if (key === 'custom') setCustomRange(r);
              }}
            />
            <Link to="/admin/customers">
              <Button>
                <Icon.plus width="14" height="14" />
                {t('ad_new_customer')}
              </Button>
            </Link>
          </div>
        </header>

        {/* KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCustomers totals={totals} />
          <Kpi
            label={t('ad_kpi_messages')}
            value={stats.messages_sent.toLocaleString(lang)}
            subtext={
              deliveryRate !== null
                ? t('ad_kpi_messages_sub', { rate: deliveryRate.toFixed(1) })
                : t('ad_kpi_messages_sub_empty')
            }
          />
          <Kpi
            label={t('ad_kpi_customer_spend')}
            value={formatCurrency(stats.customer_spend, lang)}
            subtext={t('ad_kpi_customer_spend_sub')}
          />
          <Kpi
            label={t('ad_kpi_margin')}
            value={formatCurrency(stats.reseller_margin, lang)}
            subtext={t('ad_kpi_margin_sub')}
            highlighted
          />
        </div>

        {/* Top customers + margin-by-prefix */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4 mb-6">
          <TopCustomersCard customers={top_customers} lang={lang} />
          <MarginByPrefixCard rows={margin_by_prefix} lang={lang} />
        </div>

        {/* Recent activity — last 5 audit log entries written by this
            tenant's users (signups, top-ups, suspensions, plan changes,
            etc. — anything that hits an admin mutation endpoint). */}
        <RecentActivityCard entries={recent_audit} lang={lang} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI cards
// ---------------------------------------------------------------------------
function KpiCustomers({
  totals,
}: {
  totals: { active: number; suspended: number; closed: number };
}): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <Card padding="md">
      <div className="text-[12px] text-text-3 uppercase tracking-[0.04em] font-medium">
        {t('ad_kpi_customers')}
      </div>
      <div className="num display text-[26px] font-semibold tracking-tight text-text mt-2">
        {totals.active}
      </div>
      <div className="text-[12px] text-text-3 mt-1.5">
        {t('ad_kpi_customers_sub', {
          suspended: totals.suspended,
          closed: totals.closed,
        })}
      </div>
    </Card>
  );
}

interface KpiProps {
  label: string;
  value: string;
  subtext?: string;
  highlighted?: boolean;
}
function Kpi({ label, value, subtext, highlighted = false }: KpiProps): JSX.Element {
  return (
    <Card
      padding="md"
      className={cn(highlighted && 'border-volt-200 bg-volt-50')}
    >
      <div className="text-[12px] text-text-3 uppercase tracking-[0.04em] font-medium">
        {label}
      </div>
      <div className="num display text-[26px] font-semibold tracking-tight text-text mt-2">
        {value}
      </div>
      {subtext && <div className="text-[12px] text-text-3 mt-1.5">{subtext}</div>}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Top customers card — bar chart per row, scaled to the leader's volume
// so the relative sizes match the design's "biggest customer is the
// reference" feel.
// ---------------------------------------------------------------------------
interface TopCustomersCardProps {
  customers: ResellerTopCustomer[];
  lang: string;
}
function TopCustomersCard({ customers, lang }: TopCustomersCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const max = customers.reduce((m, c) => Math.max(m, c.sent), 0);
  return (
    <Card padding="lg">
      <div className="flex justify-between items-center mb-4.5">
        <div>
          <div className="text-[14px] font-semibold text-text">
            {t('ad_top_customers_title')}
          </div>
          <div className="text-[12px] text-text-3 mt-0.5">
            {t('ad_top_customers_sub')}
          </div>
        </div>
        <Link
          to="/admin/customers"
          className="text-[12px] text-text-3 hover:text-text inline-flex items-center gap-1"
        >
          {t('ad_top_customers_view_all')}
          <Icon.arrowRight width="12" height="12" />
        </Link>
      </div>
      {customers.length === 0 ? (
        <p className="text-[13px] text-text-3 py-4 text-center">
          {t('ad_top_customers_empty')}
        </p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {customers.map((c) => (
            <TopCustomerRow key={c.id} customer={c} maxSent={max} lang={lang} />
          ))}
        </div>
      )}
    </Card>
  );
}

function TopCustomerRow({
  customer,
  maxSent,
  lang,
}: {
  customer: ResellerTopCustomer;
  maxSent: number;
  lang: string;
}): JSX.Element {
  const pct = maxSent > 0 ? (customer.sent / maxSent) * 100 : 0;
  const initial = customer.name.trim().charAt(0).toUpperCase() || '?';
  return (
    <div className="grid grid-cols-[28px_1fr_1fr_110px] gap-3 items-center">
      <span
        aria-hidden="true"
        className="w-7 h-7 rounded-md grid place-items-center text-white text-[11px] font-bold flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, var(--volt-500), var(--volt-700))' }}
      >
        {initial}
      </span>
      <Link
        to={`/admin/customers/${customer.id}`}
        className="min-w-0 hover:text-volt-700 group"
      >
        <div className="text-[13px] text-text font-medium truncate group-hover:underline underline-offset-2">
          {customer.name}
        </div>
        {customer.country && (
          <div className="mono text-[11px] text-text-3 mt-0.5">{customer.country}</div>
        )}
      </Link>
      <div className="h-2 bg-surface-3 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: 'linear-gradient(90deg, var(--volt) 0%, var(--volt-500) 100%)',
          }}
        />
      </div>
      <div className="text-right">
        <div className="num text-[13px] text-text font-semibold">
          {customer.sent.toLocaleString(lang)}
        </div>
        <div className="num text-[11px] text-text-3 mt-0.5">
          {formatCurrency(customer.spend, lang)}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Margin-by-prefix card. Rows are grouped by the dial prefix that matched
// the message in the reseller's rate cards (longest match). price/cost are
// snapshotted per message, so the margin is historically faithful even
// when rate cards change later. Rates have no country dimension, so the
// prefix is shown verbatim as "+<prefix>".
// ---------------------------------------------------------------------------
interface MarginByPrefixCardProps {
  rows: ResellerMarginPrefix[];
  lang: string;
}

function MarginByPrefixCard({ rows, lang }: MarginByPrefixCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const max = rows.reduce((m, r) => Math.max(m, r.margin), 0);
  return (
    <Card padding="lg">
      <div className="flex justify-between items-center mb-4">
        <div className="text-[14px] font-semibold text-text">
          {t('ad_margin_prefix_title')}
        </div>
        <span className="text-[11px] text-text-3 uppercase tracking-[0.04em]">
          {t('ad_margin_prefix_subtitle')}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="text-[12px] text-text-3 leading-relaxed">
          {t('ad_margin_prefix_empty')}
        </p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {rows.map((r) => {
            const pct = max > 0 ? (r.margin / max) * 100 : 0;
            const marginPct = r.revenue > 0 ? (r.margin / r.revenue) * 100 : 0;
            return (
              <div
                key={r.prefix}
                className="grid grid-cols-[72px_1fr_1fr_110px] gap-3 items-center"
              >
                <span className="mono text-[12px] text-text font-medium tracking-[0.04em]">
                  +{r.prefix}
                </span>
                <span className="text-[12px] text-text-3 truncate">
                  {t('ad_margin_prefix_messages', { count: r.messages })}
                </span>
                <div className="h-2 bg-surface-3 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      background:
                        'linear-gradient(90deg, var(--volt) 0%, var(--volt-500) 100%)',
                    }}
                  />
                </div>
                <div className="text-right">
                  <div className="num text-[13px] text-text font-semibold">
                    {formatCurrency(r.margin, lang)}
                  </div>
                  <div className="num text-[11px] text-text-3 mt-0.5">
                    {marginPct.toFixed(1)}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recent activity — last 5 audit_log entries for the calling tenant.
// Scoped to actor_tenant_id (same scope as ListAuditLog) so resellers
// never see another reseller's mutations, and customer activity stays
// on the customer's own surface.
// ---------------------------------------------------------------------------
interface RecentActivityCardProps {
  entries: ResellerRecentAuditEntry[];
  lang: string;
}
function RecentActivityCard({ entries, lang }: RecentActivityCardProps): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <Card padding="lg">
      <div className="flex justify-between items-center mb-3">
        <div className="text-[14px] font-semibold text-text">
          {t('ad_activity_title')}
        </div>
        <span className="text-[11px] text-text-3 uppercase tracking-[0.04em]">
          {t('ad_activity_subtitle')}
        </span>
      </div>
      {entries.length === 0 ? (
        <p className="text-[12px] text-text-3 leading-relaxed">
          {t('ad_activity_empty')}
        </p>
      ) : (
        <ul className="flex flex-col">
          {entries.map((e) => (
            <RecentActivityRow key={e.id} entry={e} lang={lang} />
          ))}
        </ul>
      )}
    </Card>
  );
}

// Compact one-line row. The status pill is a colour cue (success/warn/
// danger) and the path is mono so the operator can scan paths fast.
function RecentActivityRow({
  entry,
  lang,
}: {
  entry: ResellerRecentAuditEntry;
  lang: string;
}): JSX.Element {
  const tone =
    entry.status_code >= 500
      ? 'danger'
      : entry.status_code >= 400
        ? 'warn'
        : 'success';
  const toneClass = cn(
    'inline-flex items-center justify-center min-w-[36px] px-1.5 py-0.5 rounded-sm num text-[10px] font-semibold',
    tone === 'success' && 'bg-success-bg text-success border border-success-bd',
    tone === 'warn' && 'bg-warn-bg text-warn border border-warn-bd',
    tone === 'danger' && 'bg-danger-bg text-danger border border-danger-bd',
  );
  return (
    <li className="grid grid-cols-[auto_auto_1fr_auto] items-center gap-2.5 py-2 border-b border-line last:border-0">
      <span className={toneClass}>{entry.status_code}</span>
      <span className="mono text-[11px] text-text-3 uppercase tracking-[0.04em] w-[44px]">
        {entry.method}
      </span>
      <span className="mono text-[12px] text-text truncate" title={entry.path}>
        {entry.path}
      </span>
      <span
        className="text-[11px] text-text-3 num tabular-nums"
        title={`${entry.actor_email} · ${new Date(entry.created_at).toLocaleString(lang)}`}
      >
        {formatRelativeTime(entry.created_at, lang)}
      </span>
    </li>
  );
}

// Lightweight relative formatter: "just now" / "5m" / "2h" / "3d" / date.
// Uses Intl.RelativeTimeFormat for the language-specific unit copy so we
// don't add a date-fns dependency just for this widget.
function formatRelativeTime(iso: string, lang: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto', style: 'short' });
  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  if (diffSec < 3600) return rtf.format(-Math.round(diffSec / 60), 'minute');
  if (diffSec < 86400) return rtf.format(-Math.round(diffSec / 3600), 'hour');
  if (diffSec < 86400 * 7) return rtf.format(-Math.round(diffSec / 86400), 'day');
  return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short' });
}

// ---------------------------------------------------------------------------
// Low-balance banner — at the top of the page when one or more customers
// dropped below their per-tenant low_balance_threshold. Names the first
// 1-2 to give the operator something to act on.
// ---------------------------------------------------------------------------
interface LowBalanceBannerProps {
  customers: ResellerLowBalanceCustomer[];
  lang: string;
}
function LowBalanceBanner({ customers, lang }: LowBalanceBannerProps): JSX.Element {
  const { t } = useTranslation('default');
  const previewNames = customers
    .slice(0, 2)
    .map((c) => c.name)
    .join(', ');
  return (
    <div className="flex items-center gap-3 px-6 py-2.5 bg-warn-bg border-b border-warn-bd text-[13px]">
      <Icon.warn width="18" height="18" style={{ color: 'var(--warn)', flexShrink: 0 }} />
      <div className="flex-1 min-w-0">
        <strong className="text-warn font-semibold">
          {t('ad_low_balance_title', { count: customers.length })}
        </strong>
        <span className="text-text-2 ml-2">
          {customers.length === 1
            ? t('ad_low_balance_body_one', {
                name: customers[0].name,
                balance: formatCurrency(customers[0].balance, lang),
              })
            : t('ad_low_balance_body_many', { names: previewNames })}
        </span>
      </div>
      <Link to="/admin/customers">
        <Button size="sm" variant="secondary">
          {t('ad_low_balance_action')}
        </Button>
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------
function DashboardSkeleton(): JSX.Element {
  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto w-full">
      <div className="flex items-end justify-between mb-6">
        <div className="space-y-2">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-8 w-72" />
        </div>
        <Skeleton className="h-9 w-48" />
      </div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
      <div className="grid grid-cols-[1.4fr_1fr] gap-4 mb-6">
        <Skeleton className="h-[320px]" />
        <Skeleton className="h-[320px]" />
      </div>
      <Skeleton className="h-[180px]" />
    </div>
  );
}
